import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CheckStage, PreflightKey, RejectEmailRecipient } from '@hcc/types';
import { api } from '../lib/api';

/**
 * The staged screening-check workflow (DBS / DVLA) under
 * /api/admin/checks/:check_type. One row per person holding the check
 * task; HR advances rows through process -> physical verification.
 */

export interface AdminCheckRow {
  person: {
    id: string;
    full_name: string;
    email: string;
    personal_email: string | null;
    position: string | null;
    start_date: string | null;
  };
  task_id: string;
  title: string;
  stage: CheckStage;
  assigned_at: string;
  applied_at: string | null;
  processed_at: string | null;
  verified_at: string | null;
  rejection_reason: string | null;
}

export interface AdminChecksPage {
  rows: AdminCheckRow[];
  total: number;
  page: number;
  limit: number;
}

/** Invalidated by check/task socket events (see useRealtime). */
export const ADMIN_CHECKS_KEY = ['admin', 'checks'] as const;

export interface AdminChecksFilters {
  q?: string;
  stage?: CheckStage;
  page: number;
  limit?: number;
}

export function useAdminChecks(
  checkType: PreflightKey,
  filters: AdminChecksFilters,
  enabled = true,
) {
  const limit = filters.limit ?? 25;
  return useQuery({
    queryKey: [...ADMIN_CHECKS_KEY, checkType, { q: filters.q ?? '', stage: filters.stage ?? '', page: filters.page, limit }],
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.q) params.set('q', filters.q);
      if (filters.stage) params.set('stage', filters.stage);
      params.set('page', String(filters.page));
      params.set('limit', String(limit));
      return api<AdminChecksPage>(`/api/admin/checks/${checkType}?${params}`);
    },
  });
}

/**
 * Per-stage totals for one check type. Drives the sidebar badge
 * (stages.applied = pending HR review); lives under ADMIN_CHECKS_KEY so
 * the socket invalidation in useRealtime keeps it live.
 */
export function useAdminCheckCounts(checkType: PreflightKey, enabled = true) {
  return useQuery({
    queryKey: [...ADMIN_CHECKS_KEY, checkType, 'counts'],
    enabled,
    staleTime: 30_000,
    queryFn: async () =>
      (
        await api<{ stages: Record<CheckStage, number> }>(
          `/api/admin/checks/${checkType}/counts`,
        )
      ).stages,
  });
}

interface CheckActionInput {
  personId: string;
  taskId: string;
}

function useCheckAction(
  checkType: PreflightKey,
  action: 'apply' | 'process' | 'complete' | 'send-back',
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      personId,
      taskId,
      ...body
    }: CheckActionInput & {
      reason?: string;
      note?: string;
      recipient?: RejectEmailRecipient;
    }) =>
      api(`/api/admin/checks/${checkType}/${personId}/${taskId}/${action}`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (_data, { personId }) => {
      qc.invalidateQueries({ queryKey: ADMIN_CHECKS_KEY });
      qc.invalidateQueries({ queryKey: ['people', personId] });
    },
  });
}

/** HR records the application on the inductee's behalf (emailed / verbal). */
export function useMarkCheckApplied(checkType: PreflightKey) {
  return useCheckAction(checkType, 'apply');
}

/** HR confirmed the application on the external portal. */
export function useMarkCheckProcessed(checkType: PreflightKey) {
  return useCheckAction(checkType, 'process');
}

/** HR physically inspected the original certificate; completes the check. */
export function useConfirmCheck(checkType: PreflightKey) {
  return useCheckAction(checkType, 'complete');
}

/** Send the check back to the inductee with a required reason. */
export function useSendBackCheck(checkType: PreflightKey) {
  return useCheckAction(checkType, 'send-back');
}
